import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useApplicationAutosave } from "@/hooks/use-application-autosave";
import { Progress } from "@/components/ui/progress";
import { PersonalInfoStep } from "./steps/PersonalInfoStep";
import { ContactInfoStep } from "./steps/ContactInfoStep";
import { AcademicBackgroundStep } from "./steps/AcademicBackgroundStep";
import { ProgramSelectionStep } from "./steps/ProgramSelectionStep";
import { MinistryBackgroundStep } from "./steps/MinistryBackgroundStep";
import { AdditionalInfoStep } from "./steps/AdditionalInfoStep";
import { PaymentStep } from "./steps/PaymentStep";
import { Stack } from "@/components/layout/Stack";

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  gender: z.string().min(1, "Gender is required"),
  citizenship: z.string().min(1, "Citizenship is required"),
  addressStreet: z.string().min(1, "Street address is required"),
  addressCity: z.string().min(1, "City is required"),
  addressState: z.string().min(1, "State is required"),
  addressZip: z.string().min(1, "ZIP code is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(1, "Phone number is required"),
  emergencyContactName: z.string().min(1, "Emergency contact name is required"),
  emergencyContactRelationship: z.string().min(1, "Relationship is required"),
  emergencyContactPhone: z.string().min(1, "Emergency contact phone is required"),
  highestEducation: z.string().min(1, "Education level is required"),
  collegeHistory: z.array(z.object({
    institution: z.string(),
    degree: z.string(),
    graduationYear: z.string(),
    major: z.string(),
  })).optional(),
  highSchoolName: z.string().min(1, "High school name is required"),
  highSchoolGraduationYear: z.string().min(1, "Graduation year is required"),
  programInterest: z.string().min(1, "Program interest is required"),
  specificProgram: z.string().optional(),
  intendedStartDate: z.string().min(1, "Start date is required"),
  enrollmentStatus: z.string().min(1, "Enrollment status is required"),
  churchAffiliation: z.string().min(1, "Church affiliation is required"),
  pastorName: z.string().min(1, "Pastor name is required"),
  pastorContact: z.string().min(1, "Pastor contact is required"),
  yearsAsChristian: z.string().min(1, "Years as Christian is required"),
  ministryInvolvement: z.string().optional(),
  programInterestReason: z.string().min(20, "Please provide a detailed response"),
  educationUsePlan: z.string().min(20, "Please provide a detailed response"),
  referralSource: z.string().min(1, "Please tell us how you heard about us"),
  previouslyApplied: z.boolean(),
  specialAccommodations: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface ApplicationFormProps {
  onComplete: () => void;
}

export const ApplicationForm = ({ onComplete }: ApplicationFormProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const { toast } = useToast();
  const { saveDraft, restoreDraft, clearDraft } = useApplicationAutosave();
  const totalSteps = 7;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      gender: "",
      citizenship: "",
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressZip: "",
      email: "",
      phone: "",
      emergencyContactName: "",
      emergencyContactRelationship: "",
      emergencyContactPhone: "",
      highestEducation: "",
      collegeHistory: [],
      highSchoolName: "",
      highSchoolGraduationYear: "",
      programInterest: "",
      specificProgram: "",
      intendedStartDate: "",
      enrollmentStatus: "",
      churchAffiliation: "",
      pastorName: "",
      pastorContact: "",
      yearsAsChristian: "",
      ministryInvolvement: "",
      programInterestReason: "",
      educationUsePlan: "",
      referralSource: "",
      previouslyApplied: false,
      specialAccommodations: "",
    },
  });

  const progress = ((currentStep - 1) / totalSteps) * 100;

  // Restore draft on mount
  useEffect(() => {
    const draft = restoreDraft();
    if (draft) {
      console.log('[ApplicationForm] Restoring draft from localStorage');
      form.reset(draft.formData);
      setCurrentStep(draft.currentStep);
      toast({
        title: "Draft Restored",
        description: "We found your saved progress and restored it.",
      });
    }
  }, []);

  const saveProgress = async (data: Partial<FormData>, requireAuth = false) => {
    try {
      console.log('[ApplicationForm] Starting saveProgress, requireAuth:', requireAuth);
      const { data: { user } } = await supabase.auth.getUser();
      console.log('[ApplicationForm] User:', user?.id);
      
      // Only require auth if explicitly requested (e.g., at payment step)
      if (requireAuth && !user) {
        console.log('[ApplicationForm] Auth required but no user, returning null');
        return null;
      }

      // If no user, just store in memory for now (will save at payment step)
      if (!user) {
        console.log('[ApplicationForm] No user, returning null');
        return null;
      }

      // Profile is auto-created by database trigger on signup
      // Retry a few times to give the trigger time to complete
      let profile = null;
      let attempts = 0;
      const maxAttempts = 3;
      
      console.log('[ApplicationForm] Fetching profile for user:', user.id);
      while (!profile && attempts < maxAttempts) {
        const { data: fetchedProfile, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (fetchError) {
          console.error('[ApplicationForm] Error fetching profile:', fetchError);
          throw fetchError;
        }
        
        if (fetchedProfile) {
          console.log('[ApplicationForm] Profile found:', fetchedProfile.id);
          profile = fetchedProfile;
        } else if (attempts < maxAttempts - 1) {
          console.log('[ApplicationForm] Profile not found, retrying...', attempts + 1);
          // Wait before retrying (not on last attempt)
          await new Promise(resolve => setTimeout(resolve, 1000));
          attempts++;
        } else {
          // Last attempt failed
          console.log('[ApplicationForm] Profile not found after', maxAttempts, 'attempts');
          attempts++;
        }
      }
      
      if (!profile) {
        const error = 'Your account profile is still being created. Please wait a moment and try again.';
        console.error('[ApplicationForm]', error);
        throw new Error(error);
      }

      // Update profile with latest data
      console.log('[ApplicationForm] Updating profile with data');
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: data.firstName,
          last_name: data.lastName,
          phone: data.phone,
        })
        .eq('user_id', user.id);
      
      if (updateError) {
        console.error('[ApplicationForm] Error updating profile:', updateError);
        throw updateError;
      }

      // Create or update application
      console.log('[ApplicationForm] Preparing application data, existing applicationId:', applicationId);
      const applicationData = {
        profile_id: profile.id,
        status: 'draft',
        date_of_birth: data.dateOfBirth,
        gender: data.gender,
        citizenship: data.citizenship,
        address_street: data.addressStreet,
        address_city: data.addressCity,
        address_state: data.addressState,
        address_zip: data.addressZip,
        emergency_contact: {
          name: data.emergencyContactName,
          relationship: data.emergencyContactRelationship,
          phone: data.emergencyContactPhone,
        },
        highest_education: data.highestEducation,
        college_history: data.collegeHistory,
        high_school_name: data.highSchoolName,
        high_school_graduation_year: data.highSchoolGraduationYear,
        program_interest: data.programInterest,
        intended_start_date: data.intendedStartDate,
        enrollment_status: data.enrollmentStatus,
        church_affiliation: data.churchAffiliation,
        pastor_name: data.pastorName,
        pastor_contact: data.pastorContact,
        years_as_christian: data.yearsAsChristian ? parseInt(data.yearsAsChristian) : null,
        ministry_involvement: data.ministryInvolvement,
        program_interest_reason: data.programInterestReason,
        education_use_plan: data.educationUsePlan,
        referral_source: data.referralSource,
        previously_applied: data.previouslyApplied,
        special_accommodations: data.specialAccommodations,
      };

      if (applicationId) {
        console.log('[ApplicationForm] Updating existing application:', applicationId);
        const { error } = await supabase
          .from('applications')
          .update(applicationData)
          .eq('id', applicationId);
        
        if (error) {
          console.error('[ApplicationForm] Error updating application:', error);
          throw error;
        }
        console.log('[ApplicationForm] Application updated successfully');
      } else {
        console.log('[ApplicationForm] Creating new application');
        const { data: newApp, error } = await supabase
          .from('applications')
          .insert(applicationData)
          .select()
          .single();
        
        if (error) {
          console.error('[ApplicationForm] Error creating application:', error);
          throw error;
        }
        console.log('[ApplicationForm] New application created:', newApp.id);
        setApplicationId(newApp.id);
        return newApp.id;
      }
      
      return applicationId;
    } catch (error: any) {
      console.error('Error saving progress:', error);
      // Only show error toast for required saves (at payment step)
      if (requireAuth) {
        toast({
          title: "Error saving application",
          description: error.message || "Please try again or contact support if the issue persists.",
          variant: "destructive",
        });
        throw error; // Re-throw to be caught by PaymentStep
      }
      return null;
    }
  };

  const handleNext = async () => {
    const fields = getStepFields(currentStep);
    const isValid = await form.trigger(fields);
    
    if (isValid) {
      const nextStep = Math.min(currentStep + 1, totalSteps);
      setCurrentStep(nextStep);
      
      // Auto-save to localStorage
      saveDraft(form.getValues(), nextStep);
    }
  };

  const saveApplicationData = async (): Promise<string | null> => {
    const data = form.getValues();
    return await saveProgress(data, true);
  };

  const handleBack = () => {
    const prevStep = Math.max(currentStep - 1, 1);
    setCurrentStep(prevStep);
    
    // Auto-save to localStorage when going back
    saveDraft(form.getValues(), prevStep);
  };

  const getStepFields = (step: number): (keyof FormData)[] => {
    switch (step) {
      case 1:
        return ['firstName', 'lastName', 'dateOfBirth', 'gender', 'citizenship', 'addressStreet', 'addressCity', 'addressState', 'addressZip'];
      case 2:
        return ['email', 'phone', 'emergencyContactName', 'emergencyContactRelationship', 'emergencyContactPhone'];
      case 3:
        return ['highestEducation', 'highSchoolName', 'highSchoolGraduationYear'];
      case 4:
        return ['programInterest', 'intendedStartDate', 'enrollmentStatus'];
      case 5:
        return ['churchAffiliation', 'pastorName', 'pastorContact', 'yearsAsChristian', 'programInterestReason', 'educationUsePlan'];
      case 6:
        return ['referralSource', 'previouslyApplied'];
      default:
        return [];
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <PersonalInfoStep form={form} onNext={handleNext} />;
      case 2:
        return <ContactInfoStep form={form} onNext={handleNext} onBack={handleBack} />;
      case 3:
        return <AcademicBackgroundStep form={form} onNext={handleNext} onBack={handleBack} />;
      case 4:
        return <ProgramSelectionStep form={form} onNext={handleNext} onBack={handleBack} />;
      case 5:
        return <MinistryBackgroundStep form={form} onNext={handleNext} onBack={handleBack} />;
      case 6:
        return <AdditionalInfoStep form={form} onNext={handleNext} onBack={handleBack} />;
      case 7:
        return (
          <PaymentStep 
            applicationId={applicationId}
            formData={form.getValues()}
            onBack={handleBack} 
            onComplete={() => {
              clearDraft();
              onComplete();
            }}
            onSaveApplication={saveApplicationData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Stack spacing="lg">
      <div className="space-y-2" role="status" aria-live="polite">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Step {currentStep} of {totalSteps}</span>
          <span>{Math.round(progress)}% Complete</span>
        </div>
        <Progress value={progress} aria-label={`Application progress: ${Math.round(progress)}%`} />
      </div>
      
      <div role="region" aria-live="polite" aria-label={`Step ${currentStep} content`}>
        {renderStep()}
      </div>
    </Stack>
  );
};

